import React, { useState, useRef, useEffect, useContext } from 'react';
import styled from 'styled-components';
import { useNavigate, useLocation } from 'react-router-dom';
import Layout from '../Layout/Layout';
import { AuthContext } from '../../contexts/AuthContext';
import { subscriptionService } from '../../services/subscriptionService';
import { supabase } from '../../config/supabaseClient';
import Dialog from '../common/Dialog';
import { tagService } from '../../services/tagService';
import UpgradeBar from '../common/UpgradeBar';
import SearchBar from '../common/SearchBar';
import { SUBSCRIPTION_TAGS } from '../../data/constants';
import { Form, FormGroup, Label, Input, Button, ButtonGroup, Select, Required } from '../common/Form';
import { calculateNextPayment } from '../../utils/dateUtils';

const Container = styled.div`
  padding: 24px;
  animation: fadeIn 0.5s ease-out;

  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(20px); }
    to { opacity: 1; transform: translateY(0); }
  }
`;

const Title = styled.h1`
  font-size: 36px;
  margin-bottom: 24px;
  background: var(--gradient-primary);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  font-weight: 800;
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;

  @media (max-width: 968px) {
    grid-template-columns: 1fr;
  }
`;

const PriceInput = styled.div`
  position: relative;

  input {
    padding-left: 24px;
  }

  &::before {
    content: '$';
    position: absolute;
    left: 12px;
    top: 50%;
    transform: translateY(-50%);
    color: var(--text-secondary);
  }
`;

const SelectWrapper = styled.div`
  position: relative;
  width: 100%;

  &::after {
    content: '▼';
    position: absolute;
    right: 12px;
    top: 50%;
    transform: translateY(-50%);
    color: var(--text-secondary);
    pointer-events: none;
    font-size: 12px;
  }
`;

const StyledSelect = styled.select`
  width: 100%;
  padding: 12px;
  border: 1px solid var(--border);
  border-radius: 8px;
  background: var(--surface);
  color: var(--text-primary);
  font-size: 16px;
  appearance: none;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: var(--primary);
    box-shadow: 0 0 0 2px var(--primary-light);
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }

  option {
    background: var(--surface);
    color: var(--text-primary);
    padding: 8px;
  }
`;

const TagsWrapper = styled.div`
  position: relative;
  width: 100%;
`;

const TagsInput = styled.div`
  min-height: 48px;
  padding: 8px 12px;
  border: 1px solid var(--border);
  border-radius: 8px;
  background: var(--surface);
  color: var(--text-primary);
  cursor: pointer;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;

  &:focus-within {
    border-color: var(--primary);
    box-shadow: 0 0 0 2px var(--primary-light);
  }
`;

const TagItem = styled.span`
  background: var(--primary);
  color: white;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 4px;
`;

const RemoveTag = styled.span`
  cursor: pointer;
  &:hover {
    opacity: 0.8;
  }
`;

const TagsDropdown = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  margin-top: 4px;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  z-index: 1000;
  max-height: 250px;
  overflow-y: auto;
`;

const TagOption = styled.div`
  padding: 8px 12px;
  cursor: pointer;
  color: var(--text-primary);
  background: ${props => props.selected ? 'var(--hover)' : 'transparent'};

  &:hover {
    background: var(--hover);
  }
`;

const PlaceholderText = styled.span`
  color: var(--text-secondary);
`;

const TagSection = styled.div`
  padding: 8px 0;

  &:not(:last-child) {
    border-bottom: 1px solid var(--border);
  }
`;

const TagSectionTitle = styled.div`
  padding: 8px 12px;
  font-size: 12px;
  color: var(--text-secondary);
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const CreateSubscription = () => {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const { state } = useLocation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    provider: '',
    price: '',
    cycle: 'Monthly',
    startDate: new Date().toISOString().split('T')[0],
    paymentMethod: '',
    tags: [],
    type: 'Personal',
    category: '',
    logo: '',
  });
  const [selectedTags, setSelectedTags] = useState([]);
  const [showTagsDropdown, setShowTagsDropdown] = useState(false);
  const tagsRef = useRef(null);
  const [userTags, setUserTags] = useState([]);

  useEffect(() => {
    const fetchUserTags = async () => {
      if (!user) return;
      
      try {
        // Get user-created tags
        const userCreatedTags = await tagService.getAllTags(user.id);
        
        // Format user tags to match the structure of SUBSCRIPTION_TAGS
        const formattedUserTags = userCreatedTags.map(tag => ({
          value: tag.name.toLowerCase(), // Use tag name as value
          label: tag.name,
          isUserTag: true
        }));

        // Combine user tags with default subscription tags
        const allTags = [...formattedUserTags, ...SUBSCRIPTION_TAGS];
        
        // Remove duplicates based on value
        const uniqueTags = allTags.filter((tag, index, self) =>
          index === self.findIndex(t => t.value === tag.value)
        );
        
        setUserTags(uniqueTags);
        console.log('All available tags:', uniqueTags); // Debug log
      } catch (error) {
        console.error('Error fetching tags:', error);
        setUserTags(SUBSCRIPTION_TAGS);
      }
    };

    fetchUserTags();
  }, [user]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (tagsRef.current && !tagsRef.current.contains(event.target)) {
        setShowTagsDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    console.log('Current user:', user);
  }, [user]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleTagSelect = (tagValue) => {
    setFormData(prev => {
      const newTags = prev.tags.includes(tagValue)
        ? prev.tags.filter(t => t !== tagValue)
        : [...prev.tags, tagValue];
      
      return {
        ...prev,
        tags: newTags
      };
    });
  };

  const removeTag = (tagToRemove) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  const handleCompanyClick = (company) => {
    setFormData(prev => ({
      ...prev,
      category: company.category,
      logo: company.logo,
      tags: [...prev.tags, company.category.toLowerCase()]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setIsSubmitting(true);

      const nextPayment = calculateNextPayment(formData.startDate, formData.cycle);

      const newSubscription = {
        user_id: user.id,
        name: formData.name.trim(),
        provider: formData.provider.trim(),
        price: parseFloat(formData.price),
        cycle: formData.cycle,
        payment_method: formData.paymentMethod,
        start_date: new Date(formData.startDate).toISOString(),
        next_payment: nextPayment,
        created_at: new Date().toISOString(),
        tags: formData.tags
      };

      await subscriptionService.createSubscription(newSubscription);
      navigate('/subscriptions', { state: { refresh: true } });
    } catch (error) {
      console.error('Error creating subscription:', error);
      alert(error.message || 'Failed to create subscription. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSuccessClose = () => {
    setShowSuccess(false);
    navigate('/subscriptions', { 
      replace: true,
      state: { refresh: true }
    });
  };

  return (
    <Layout>
      <Container>
        <UpgradeBar />
        <Title>Create Subscription</Title>
        <Form onSubmit={handleSubmit}>
          <FormRow>
            <FormGroup>
              <Label>Name<Required>*</Required></Label>
              <Input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
              />
            </FormGroup>

            <FormGroup>
              <Label>Provider<Required>*</Required></Label>
              <Input
                type="text"
                name="provider"
                value={formData.provider}
                onChange={handleChange}
                required
              />
            </FormGroup>

            <FormGroup>
              <Label>Type<Required>*</Required></Label>
              <SelectWrapper>
                <StyledSelect
                  name="type"
                  value={formData.type}
                  onChange={handleChange}
                  required
                >
                  <option value="Personal">Personal</option>
                  <option value="Business">Business</option>
                  <option value="Family">Family</option>
                </StyledSelect>
              </SelectWrapper>
            </FormGroup>
          </FormRow>

          <FormRow>
            <FormGroup>
              <Label>Price<Required>*</Required></Label>
              <PriceInput>
                <Input
                  type="number"
                  name="price"
                  value={formData.price}
                  onChange={handleChange}
                  required
                  min="0"
                  step="0.01"
                />
              </PriceInput>
            </FormGroup>

            <FormGroup>
              <Label>Payment Method<Required>*</Required></Label>
              <SelectWrapper>
                <StyledSelect
                  name="paymentMethod"
                  value={formData.paymentMethod}
                  onChange={handleChange}
                  required
                >
                  <option value="">Select a payment method</option>
                  <option value="credit">Credit Card</option>
                  <option value="debit">Debit Card</option>
                  <option value="paypal">PayPal</option>
                </StyledSelect>
              </SelectWrapper>
            </FormGroup>

            <FormGroup>
              <Label>Cycle<Required>*</Required></Label>
              <SelectWrapper>
                <StyledSelect
                  name="cycle"
                  value={formData.cycle}
                  onChange={handleChange}
                  required
                >
                  <option value="Monthly">Monthly</option>
                  <option value="Yearly">Yearly</option>
                  <option value="Weekly">Weekly</option>
                </StyledSelect>
              </SelectWrapper>
            </FormGroup>
          </FormRow>

          <FormRow>
            <FormGroup>
              <Label>Company<Required>*</Required></Label>
              <SearchBar 
                onSelect={handleCompanyClick}
              />
            </FormGroup>

            <FormGroup>
              <Label>Tags</Label>
              <TagsWrapper ref={tagsRef}>
                <TagsInput onClick={() => setShowTagsDropdown(!showTagsDropdown)}>
                  {formData.tags.length > 0 ? (
                    formData.tags.map(tagValue => {
                      const tag = userTags.find(t => t.value === tagValue);
                      return (
                        <TagItem key={tagValue}>
                          {tag?.label}
                          <RemoveTag
                            onClick={(e) => {
                              e.stopPropagation();
                              removeTag(tagValue);
                            }}
                          >
                            ×
                          </RemoveTag>
                        </TagItem>
                      );
                    })
                  ) : (
                    <PlaceholderText>Select tags...</PlaceholderText>
                  )}
                </TagsInput>

                {showTagsDropdown && (
                  <TagsDropdown>
                    {userTags.length > 0 ? (
                      <>
                        {/* User created tags section */}
                        {userTags.filter(tag => tag.isUserTag).length > 0 && (
                          <TagSection>
                            <TagSectionTitle>Your Tags</TagSectionTitle>
                            {userTags
                              .filter(tag => tag.isUserTag)
                              .map(tag => (
                                <TagOption
                                  key={tag.value}
                                  selected={formData.tags.includes(tag.value)}
                                  onClick={() => handleTagSelect(tag.value)}
                                >
                                  {tag.label}
                                </TagOption>
                              ))}
                          </TagSection>
                        )}
                        
                        {/* Default tags section */}
                        <TagSection>
                          <TagSectionTitle>Default Tags</TagSectionTitle>
                          {userTags
                            .filter(tag => !tag.isUserTag)
                            .map(tag => (
                              <TagOption
                                key={tag.value}
                                selected={formData.tags.includes(tag.value)}
                                onClick={() => handleTagSelect(tag.value)}
                              >
                                {tag.label}
                              </TagOption>
                            ))}
                        </TagSection>
                      </>
                    ) : (
                      <TagOption>No tags available</TagOption>
                    )}
                  </TagsDropdown>
                )}
              </TagsWrapper>
            </FormGroup>

            <FormGroup>
              <Label>Start date<Required>*</Required></Label>
              <Input
                type="date"
                name="startDate"
                value={formData.startDate}
                onChange={handleChange}
                required
              />
            </FormGroup>
          </FormRow>

          <ButtonGroup>
            <Button 
              type="submit" 
              primary 
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Creating...' : 'Save & Exit'}
            </Button>
            <Button 
              type="button" 
              secondary 
              onClick={() => navigate('/subscriptions')}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
          </ButtonGroup>
        </Form>

        <Dialog
          isOpen={showSuccess}
          onClose={handleSuccessClose}
          title="Success! 🎉"
          message="Your subscription has been created successfully!"
          icon="💫"
        />
      </Container>
    </Layout>
  );
};

export default CreateSubscription; 